import { useMemo } from 'react';

import { graphql } from '@/__generated__';
import { EnvironmentSummaryQuery } from '@/__generated__/graphql';
import { useLazyQuery } from '../useLazyQuery';
import { useQueryStable } from '../useQueryStable';

// Helper
export const aggregateEnvironmentSummary = (
  summaries: EnvironmentSummary[],
  projectName?: string,
) => {
  const aggregated: EnvironmentSummary = summaries.reduce(
    (acc, summary) => ({
      environment_id: acc.environment_id || summary.environment_id,
      dbt_project_name: projectName || acc.dbt_project_name || summary.dbt_project_name,
      exposure: acc.exposure + summary.exposure,
      model: acc.model + summary.model,
      public_model: acc.public_model + summary.public_model,
      source: acc.source + summary.source,
      seed: acc.seed + summary.seed,
      snapshot: acc.snapshot + summary.snapshot,
      test: acc.test + summary.test,
      groups: acc.groups + summary.groups,
      macro: acc.macro + summary.macro,
      metric: acc.metric + summary.metric,
      semantic_model: acc.semantic_model + summary.semantic_model,
      saved_query: acc.saved_query + summary.saved_query,
    }),
    {
      environment_id: 0,
      dbt_project_name: '',
      exposure: 0,
      model: 0,
      public_model: 0,
      source: 0,
      seed: 0,
      snapshot: 0,
      test: 0,
      groups: 0,
      macro: 0,
      metric: 0,
      semantic_model: 0,
      saved_query: 0,
    } satisfies EnvironmentSummary,
  );
  return aggregated;
};

export const GetEnvironmentSummaries = graphql(/* GraphQL */ `
  query EnvironmentSummary($environmentIds: [BigInt!]!, $accountId: BigInt!) {
    account(id: $accountId) {
      environmentSummary(environmentIds: $environmentIds) {
        environmentId
        dbtProjectName
        resourceCounts
      }
    }
  }
`);

type EnvironmentSummaryProps = {
  accountId: number | undefined;
  environmentId: number | null | undefined;
  pause?: boolean;
};
type EnvironmentSummariesProps = {
  accountId: number | undefined;
  environmentIds: number[] | undefined;
  pause?: boolean;
};

export type EnvironmentSummary = {
  environment_id: number;
  dbt_project_name: string;
  exposure: number;
  model: number;
  public_model: number;
  source: number;
  seed: number;
  snapshot: number;
  test: number;
  groups: number;
  macro: number;
  metric: number;
  semantic_model: number;
  saved_query: number;
};

export const useEnvironmentSummaries = ({
  accountId = 0,
  environmentIds = [],
  pause,
}: EnvironmentSummariesProps) => {
  const [query] = useQueryStable({
    query: GetEnvironmentSummaries,
    variables: { accountId, environmentIds },
    pause: pause || !accountId || !environmentIds.length,
  });

  return useMemo(() => {
    const data = query.data?.account.environmentSummary.map(
      (summary) =>
        ({
          environment_id: Number(summary?.environmentId || 0),
          dbt_project_name: summary?.dbtProjectName || '',
          exposure: Number(summary?.resourceCounts?.dbt_exposure_definition_state) || 0,
          model: Number(summary?.resourceCounts?.dbt_model_applied_state) || 0,
          public_model:
            Number(summary?.resourceCounts?.public_model_applied_state) || 0,
          source: Number(summary?.resourceCounts?.dbt_source_applied_state) || 0,
          seed: Number(summary?.resourceCounts?.dbt_seed_applied_state) || 0,
          snapshot: Number(summary?.resourceCounts?.dbt_snapshot_applied_state) || 0,
          test: Number(summary?.resourceCounts?.dbt_test_applied_state) || 0,
          groups: Number(summary?.resourceCounts?.dbt_group) || 0,
          macro: Number(summary?.resourceCounts?.dbt_macro_definition_state) || 0,
          metric: Number(summary?.resourceCounts?.dbt_metric_definition_state) || 0,
          semantic_model:
            Number(summary?.resourceCounts?.dbt_semantic_model_definition_state) || 0,
          saved_query:
            Number(summary?.resourceCounts?.dbt_saved_query_definition_state) || 0,
        }) satisfies EnvironmentSummary,
    );
    return { ...query, data: query.data ? data : null };
  }, [query]);
};

export const useEnvironmentSummary = ({
  accountId = 0,
  environmentId = 0,
  pause,
}: EnvironmentSummaryProps) => {
  const summaries = useEnvironmentSummaries({
    accountId,
    environmentIds: environmentId ? [environmentId] : [],
    pause,
  });

  return useMemo(() => {
    if (!summaries.data || summaries.data.length === 0) {
      return { ...summaries, data: undefined };
    }

    // Sum all numeric values across all environments
    const aggregated = aggregateEnvironmentSummary(summaries.data);

    return { ...summaries, data: aggregated };
  }, [summaries]);
};

export const useEnvironmentSummariesLazy = () => {
  const [environmentSummaries, queryExecute] = useLazyQuery(GetEnvironmentSummaries);

  const transformData = (data: EnvironmentSummaryQuery | undefined) => {
    if (!data?.account?.environmentSummary) return null;
    return data.account.environmentSummary.map(
      (summary) =>
        ({
          environment_id: Number(summary?.environmentId || 0),
          dbt_project_name: summary?.dbtProjectName || '',
          exposure: Number(summary?.resourceCounts?.dbt_exposure_definition_state) || 0,
          model: Number(summary?.resourceCounts?.dbt_model_applied_state) || 0,
          public_model:
            Number(summary?.resourceCounts?.public_model_applied_state) || 0,
          source: Number(summary?.resourceCounts?.dbt_source_applied_state) || 0,
          seed: Number(summary?.resourceCounts?.dbt_seed_applied_state) || 0,
          snapshot: Number(summary?.resourceCounts?.dbt_snapshot_definition_state) || 0,
          test: Number(summary?.resourceCounts?.dbt_test_applied_state) || 0,
          groups: Number(summary?.resourceCounts?.dbt_group) || 0,
          macro: Number(summary?.resourceCounts?.dbt_macro_definition_state) || 0,
          metric: Number(summary?.resourceCounts?.dbt_metric_definition_state) || 0,
          semantic_model:
            Number(summary?.resourceCounts?.dbt_semantic_model_definition_state) || 0,
          saved_query:
            Number(summary?.resourceCounts?.dbt_saved_query_definition_state) || 0,
        }) satisfies EnvironmentSummary,
    );
  };

  const executeQuery = async (args: EnvironmentSummariesProps) => {
    const res = await queryExecute({
      accountId: args.accountId ?? 0,
      environmentIds: args.environmentIds ?? [],
    });

    return {
      ...res,
      data: transformData(res.data),
      fetching: false,
    };
  };

  return [
    {
      ...environmentSummaries,
      data: transformData(environmentSummaries.data),
    },
    executeQuery,
  ] as const;
};
